// Re-export NotificationChannelType as ChannelType for backwards compatibility
// Canonical source: src/pages/alerts/types.ts
export { NotificationChannelType as ChannelType } from "@/pages/alerts/types";

export const SEARCH_PARAMS = {
  PAGE: "page",
  PAGE_SIZE: "page_size",
  CATEGORY: "category",
  RULE_TYPE: "rule_type",
  PLATFORM: "platform",
  CREATED_BY: "created_by",
  DESTINATION: "destination",
  SEARCH: "search",
  SORT_BY: "sort_by",
  SORT: "sort",
  START_DATE: "start_date",
  END_DATE: "end_date",
  STATUS: "status",
};

export const TIME_PERIODS = [
  { label: "Last Day", period: 1 },
  { label: "Last Week", period: 7 },
  { label: "Last Month", period: 30 },
];

// Enums for type safety

/** Subscription types */
export enum SubscriptionType {
  REPORT = "report",
  ALERT = "alert",
}

/** Rule source discriminator (server-side ``RuleResponse.source``).
 *
 * - METRIC — the original metric-alert rule (uses ``metric_config``,
 *   ``platform``, ``entity_type``).
 * - PLAYGROUND_REPORT — a recurring Studio chat report; ``session_id`` and
 *   ``content_scope`` are populated instead of the metric fields.
 */
export enum RuleSource {
  METRIC = "metric",
  PLAYGROUND_REPORT = "playground_report",
}

/** Category label for playground_report rows. They carry no server-side
 *  ``category`` (it's a metric-rule concept), so the list column would
 *  otherwise fall through to the "Custom SQL" default. */
export const STUDIO_REPORT_CATEGORY_LABEL = "Studio Report";

/** Content scope for a playground_report rule — which slice of the
 * Studio session gets rendered into the recurring email/slack payload. */
export enum PlaygroundReportContentScope {
  FIRST_QUERY = "first_query",
  LAST_ANSWER = "last_answer",
  FULL_CHAT = "full_chat",
}

/** Wizard step identifiers */
export enum WizardStepEnum {
  SUBSCRIPTION_TYPE = "subscription-type",
  CUSTOMIZE = "customize",
  DESTINATION = "destination",
}

/** Subscription status values */
export enum SubscriptionStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

/** Run/execution status values for subscription history.
 *
 * Alert subscriptions: TRIGGERED (condition met) or SKIPPED (condition not met).
 * Report subscriptions: SENT (delivered).
 * Either: FAILURE (execution error), or DEFERRED.
 *
 * DEFERRED is not a failure and not "condition not met" — the check never ran,
 * because the source data wasn't complete for the window the run covers.
 * Nothing was evaluated and nothing was sent; the backend retries every 30
 * minutes for up to 16 attempts. If the data never lands the run settles as
 * FAILURE with ``error_code: "data_not_ready_abandoned"``, so a run sitting at
 * DEFERRED always means "in progress, delivery is late".
 */
export enum RunStatus {
  FAILURE = "failure",
  TRIGGERED = "triggered",
  SKIPPED = "skipped",
  SENT = "sent",
  DEFERRED = "deferred",
  // Seen in a production payload (ALTIMATE-FRONTEND-168); not written by the
  // known backend run-history paths, so how it arrives is unverified.
  SUCCESS = "success",
}

/** User-facing label for {@link RunStatus.DEFERRED}. "Deferred" reads as a
 *  decision someone made; what the user needs to know is that delivery is
 *  late. Shared by the history badge, the status filter and the recent-runs
 *  icon tooltip so they can't drift. */
export const DEFERRED_RUN_LABEL = "Delayed";

/** History dropdown filter values.
 *
 * Superset of `RunStatus` plus `ALL` (no filter). Used by the History
 * status dropdown (`HistorySection`), the URL `?status=` validator
 * (`useSubscriptionDetail`), and any other site that drives the
 * history filter UI. Concrete-status values are intentionally the same
 * strings as `RunStatus` so the discriminator can be compared against
 * either enum without coercion.
 */
export enum StatusFilter {
  ALL = "all",
  TRIGGERED = "triggered",
  SKIPPED = "skipped",
  SENT = "sent",
  FAILURE = "failure",
  DEFERRED = "deferred",
}

/** History status-dropdown options per rule kind. Also the allowlist the
 *  ``?status=`` URL validator derives from, so the two can't drift. DEFERRED
 *  appears for both kinds — a run can be delayed on data whether it would
 *  have alerted or reported. */
export const ALERT_STATUS_FILTER_OPTIONS = [
  { label: "All Status", value: StatusFilter.ALL },
  { label: "Triggered", value: StatusFilter.TRIGGERED },
  { label: "Skipped", value: StatusFilter.SKIPPED },
  { label: DEFERRED_RUN_LABEL, value: StatusFilter.DEFERRED },
  { label: "Failed", value: StatusFilter.FAILURE },
];

export const REPORT_STATUS_FILTER_OPTIONS = [
  { label: "All Status", value: StatusFilter.ALL },
  { label: "Sent", value: StatusFilter.SENT },
  { label: DEFERRED_RUN_LABEL, value: StatusFilter.DEFERRED },
  { label: "Failed", value: StatusFilter.FAILURE },
];

/** Rule kind not yet known — keep it to values valid for either. */
export const DEFAULT_STATUS_FILTER_OPTIONS = [
  { label: "All Status", value: StatusFilter.ALL },
  { label: DEFERRED_RUN_LABEL, value: StatusFilter.DEFERRED },
  { label: "Failed", value: StatusFilter.FAILURE },
];

/** Variable types used in subscription prompts */
export enum VariableType {
  AGGREGATION = "aggregation",
  DATE_RANGE = "date_range",
  COMPARISON = "comparison",
  CHANGE_TYPE = "change_type",
  THRESHOLD = "threshold",
  METRIC = "metric",
  ENTITY = "entity",
  SCHEDULE = "schedule",
  FILTER = "filter",
}

/** Preview item content types */
export enum PreviewItemType {
  MARKDOWN = "markdown",
  CHART = "chart",
}

/** Change type values for alerts */
export enum ChangeType {
  RELATIVE_VALUE = "relative_value",
  ABSOLUTE_VALUE = "absolute_value",
}

/** Report type restrictions — reports only support these specific options per source. */
export const REPORT_RESTRICTIONS = {
  BY_SOURCE: {
    snowflake: {
      ALLOWED_CATEGORIES: ["account"],
      ALLOWED_METRICS: ["account_overall_cost"],
      ALLOWED_FILTERS: ["instance"],
    },
    databricks: {
      ALLOWED_CATEGORIES: ["account"],
      ALLOWED_METRICS: ["dbx_account_total_cost"],
      ALLOWED_FILTERS: ["instance"],
    },
  },
} as const;

/** Comparison type values for alerts */
export enum ComparisonType {
  CHANGES_BY = "changes_by",
  EXCEEDS = "exceeds",
  BELOW = "below",
  CHANGES_BY_PERCENT = "changes_by_percent",
}

/** Date range values for subscriptions */
export enum DateRange {
  PREVIOUS_DAY = "previous_day",
  PREVIOUS_WEEK = "previous_week",
  PREVIOUS_MONTH = "previous_month",
  PREVIOUS_YEAR = "previous_year",
  PREVIOUS_DAY_TRAILING_7_DAY_AVG = "previous_day_trailing_7_day_avg",
}

/** Who a rule is visible to, and at what level.
 *
 * - ``private``       — creator only.
 * - ``team_readonly`` — the rule's team can see it; only the creator can edit.
 * - ``team``          — the rule's team can see, edit and pause it.
 *
 * Delete and re-sharing are creator-only at every tier. The team itself is
 * derived server-side from the ``x-user-group-id`` header and is never sent
 * in a request body. */
export enum RuleVisibility {
  PRIVATE = "private",
  TEAM_READONLY = "team_readonly",
  TEAM = "team",
}

/** True for both shared tiers — i.e. the rule is visible beyond its creator. */
export const isSharedVisibility = (visibility: RuleVisibility) =>
  visibility === RuleVisibility.TEAM || visibility === RuleVisibility.TEAM_READONLY;
